/* =========================================================
   DCG HEADER — ROW 1
   File: assets/dcg-header.js
   Version: 1.2 — 03 AUG 2026

   Handles:
   1. Condense-on-scroll (desktop) — adds .is-condensed to .dcg-header
   2. Mobile search overlay — open / close / focus / keyboard / outside-tap
   3. --dcg-mobile-bar-h measurement

   ── v1.1 — mobile scroll-lock leak fix (F-PERF-1) ─────────────────────
   The search overlay has two controllers: this file (owns the .is-open
   class) and dcg-search-overlay.js (owns the results UI). Only the latter
   was managing document.documentElement.style.overflow, and this file's
   close paths never cleared it.

   ROOT CAUSE 1 — dead close-button selector.
     This file queried '[data-dcg-search-close]'. The markup at
     sections/header-top.liquid:470 uses 'data-dcg-close'. The binding
     matched nothing, so the X button was handled only by
     dcg-search-overlay.js — which happens to be the one path that DID
     clear the scroll lock. Every other dismissal leaked it.

   ROOT CAUSE 2 — Escape and backdrop taps stripped .is-open first.
     This file loads at sections/header-top.liquid:277; dcg-search-overlay.js
     loads at layout/theme.liquid:621. defer preserves document order, so
     this file's document-level keydown listener is registered FIRST and
     removes .is-open before the overlay's own Escape handler runs. That
     handler guards on overlay.classList.contains('is-open'), sees false,
     and never calls close() — so overflow:hidden was left applied.
     Same for the backdrop tap and the toggle-button re-tap.

     Net effect: on a phone, dismissing the search overlay by any route
     except the X button left <html> permanently unscrollable until reload.

   FIX — this file now owns the scroll lock symmetrically: openSearch()
   sets it, closeSearch() clears it. dcg-search-overlay.js still writes the
   same two values, which is idempotent and harmless — whichever runs last
   writes the correct state. The close button is now handled by delegation
   matching BOTH attribute names via closest(), so taps on the inner <svg>
   register too.
   ========================================================= */

(() => {
  'use strict';

  (window.DCG = window.DCG || {}).headerVersion = '1.2';

  /* -------------------------------------------------------
     REFS
     ------------------------------------------------------- */
  const header       = document.querySelector('.dcg-header');
  const mobileBar    = document.querySelector('.dcg-mobile-bar');
  const overlay      = document.getElementById('dcgSearchOverlay');
  const searchToggle = document.querySelector('[data-dcg-search-toggle]');
  const root         = document.documentElement;

  /* Close-button selector. 'data-dcg-close' is what the markup actually
     emits (header-top.liquid:470); 'data-dcg-search-close' is kept as an
     alias so a future markup rename in either direction still resolves. */
  const CLOSE_SEL = '[data-dcg-close], [data-dcg-search-close]';

  /* -------------------------------------------------------
     1. MOBILE BAR HEIGHT
     Measures the bar and writes --dcg-mobile-bar-h so the
     page-container padding and overlay top are always correct.
     ------------------------------------------------------- */
  function measureMobileBar() {
    if (!mobileBar) return;
    // Only measure when bar is actually rendered (mobile viewport)
    const visible = window.getComputedStyle(mobileBar).display !== 'none';
    if (!visible) return;
    const h = Math.ceil(mobileBar.getBoundingClientRect().height);
    root.style.setProperty('--dcg-mobile-bar-h', `${h}px`);
  }

  // Run once and on resize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', measureMobileBar);
  } else {
    measureMobileBar();
  }
  window.addEventListener('resize', measureMobileBar);


  /* -------------------------------------------------------
     2. CONDENSE ON SCROLL (desktop)
     Adds/removes .is-condensed on .dcg-header.
     Threshold: 100px of scroll.
     ------------------------------------------------------- */
  const CONDENSE_AT = 100;

  function updateCondense() {
    if (!header) return;
    header.classList.toggle('is-condensed', window.scrollY > CONDENSE_AT);
  }

  let scrollTicking = false;

  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(() => {
      updateCondense();
      scrollTicking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  updateCondense(); // initial state


  /* -------------------------------------------------------
     3. MOBILE SEARCH OVERLAY
     ------------------------------------------------------- */
  if (!overlay) return; // nothing to do on pages without overlay

  /* Scroll lock. Written by both this file and dcg-search-overlay.js with
     identical values — safe to call redundantly. Always paired: any path
     that adds .is-open locks, any path that removes it unlocks. */
  function lockScroll() {
    root.style.overflow = 'hidden';
  }

  function unlockScroll() {
    root.style.overflow = '';
  }

  function openSearch() {
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    searchToggle?.setAttribute('aria-expanded', 'true');
    lockScroll();

    // Focus the input after transition starts
    const input = overlay.querySelector('.search-header__input, input[type="search"]');
    if (input) {
      setTimeout(() => {
        input.focus({ preventScroll: true });
      }, 60);
    }
  }

  function closeSearch() {
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    searchToggle?.setAttribute('aria-expanded', 'false');
    unlockScroll();
    searchToggle?.focus({ preventScroll: true });
  }

  function toggleSearch() {
    overlay.classList.contains('is-open') ? closeSearch() : openSearch();
  }

  // Search toggle button
  searchToggle?.addEventListener('click', toggleSearch);

  /* Close button + backdrop tap, via delegation on the overlay.
     closest() so taps on the <svg> inside the close button still register.
     Replaces the previous direct '[data-dcg-search-close]' binding, which
     matched nothing because the markup uses data-dcg-close. */
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) { closeSearch(); return; }          // backdrop
    if (e.target.closest && e.target.closest(CLOSE_SEL)) closeSearch(); // X button
  });

  /* Outside-tap dismissal — v1.2.
     The overlay is a bounded dropdown anchored below the mobile bar
     (dcg-header.css:933-947), NOT a full-screen scrim. So the
     `e.target === overlay` backdrop check above can only ever fire on the thin
     strip of overlay padding between its own children — in practice never.

     That left the X button and the magnifier as the only two exits, with the
     page scroll-locked the whole time. A customer who spotted neither was
     stuck until they reloaded.

     Bubble phase, with the toggle excluded: the toggle's own click handler
     runs first at target phase, so without that guard opening the overlay
     would immediately re-close it. */
  document.addEventListener('click', (e) => {
    if (!overlay.classList.contains('is-open')) return;
    if (!e.target || !e.target.closest) return;
    if (overlay.contains(e.target)) return;                    // inside the panel
    if (e.target.closest('[data-dcg-search-toggle]')) return;  // the toggle itself
    closeSearch();
  });

  // Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
      closeSearch();
    }
  });

  // Close search if viewport grows past mobile breakpoint (e.g. rotation)
  const mobileQuery = window.matchMedia('(max-width: 991px)');
  mobileQuery.addEventListener('change', (e) => {
    if (!e.matches && overlay.classList.contains('is-open')) {
      closeSearch();
    }
  });

  /* Safety net: if anything else strips .is-open without going through
     closeSearch(), release the lock rather than stranding the page.
     Cheap — fires only on class changes to the overlay element itself. */
  new MutationObserver(() => {
    if (!overlay.classList.contains('is-open') && root.style.overflow === 'hidden') {
      unlockScroll();
    }
  }).observe(overlay, { attributes: true, attributeFilter: ['class'] });

})();
